import { PanelsTopLeft } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import BlindsPredictionCorrection from '../components/BlindsPredictionCorrection';
import TablePagination from '../components/TablePagination';
import { useDebouncedValue } from '../hooks/useDebouncedValue';
import { usePagination } from '../hooks/usePagination';
import { blindsTrackingService } from '../services/api';

const ProductionBlindsPredictions = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('item_number');
  const [sortDir, setSortDir] = useState('asc');
  const fetchIdRef = useRef(0);
  const debouncedSearchTerm = useDebouncedValue(searchTerm, 250);
  const activeSearchTerm = searchTerm.trim() === '' ? '' : debouncedSearchTerm;

  const fetchData = useCallback(async () => {
    const fetchId = ++fetchIdRef.current;
    setLoading(true);
    setError('');
    try {
      const result = await blindsTrackingService.getPredictions();
      if (fetchId === fetchIdRef.current) setData(result || []);
    } catch (err) {
      if (fetchId === fetchIdRef.current) {
        setError(err.response?.data?.detail || 'Nie udało się pobrać predykcji rolet.');
      }
    } finally {
      if (fetchId === fetchIdRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    return () => { fetchIdRef.current += 1; };
  }, [fetchData]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(current => (current === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const sortIndicator = field => (
    sortField === field ? (sortDir === 'asc' ? ' ▲' : ' ▼') : ''
  );

  const processedData = useMemo(() => {
    const term = activeSearchTerm.trim().toLocaleLowerCase('pl-PL');
    const items = term
      ? data.filter(item => [
        item.item_number,
        item.item_name,
        item.supplier,
        item.business_days,
        item.correction_days,
      ].some(value => String(value ?? '').toLocaleLowerCase('pl-PL').includes(term)))
      : [...data];

    items.sort((a, b) => {
      const left = a[sortField];
      const right = b[sortField];
      let comparison;
      if (typeof left === 'number' && typeof right === 'number') {
        comparison = left - right;
      } else {
        comparison = String(left ?? '').localeCompare(String(right ?? ''), 'pl', {
          numeric: true,
          sensitivity: 'base',
        });
      }
      return sortDir === 'asc' ? comparison : -comparison;
    });
    return items;
  }, [activeSearchTerm, data, sortDir, sortField]);

  const pagination = usePagination(processedData, {
    storageKey: 'production-blinds-predictions-page-size',
  });

  const updateCorrection = correction => setData(items => items.map(item => (
    item.item_number === correction.item_number ? { ...item, ...correction } : item
  )));

  return (
    <div className="page-container order-monitoring">
      <div className="page-header">
        <h2 className="module-page-title"><PanelsTopLeft aria-hidden="true" /> Predykcja dostaw rolet</h2>
        <p className="header-description">Bazowe czasy dostaw materiałów roletowych i czasowe korekty.</p>
      </div>
      <div className="page-content">
        <div className="om-search-row">
          <input
            className="om-search"
            type="search"
            placeholder="Wyszukaj po indeksie, nazwie lub dostawcy"
            aria-label="Wyszukaj predykcje rolet"
            value={searchTerm}
            onChange={event => setSearchTerm(event.target.value)}
          />
        </div>

        {error && <div className="om-banner om-banner--error" role="alert">{error}</div>}
        {loading ? (
          <div className="om-loading">Ładowanie predykcji…</div>
        ) : processedData.length === 0 ? (
          <div className="om-empty">Brak predykcji spełniających kryteria.</div>
        ) : (
          <>
            <div className="om-table-wrap">
              <table className="om-table">
                <thead>
                  <tr>
                    <th onClick={() => handleSort('item_number')} style={{ cursor: 'pointer' }}>Indeks{sortIndicator('item_number')}</th>
                    <th onClick={() => handleSort('item_name')} style={{ cursor: 'pointer' }}>Nazwa{sortIndicator('item_name')}</th>
                    <th onClick={() => handleSort('supplier')} style={{ cursor: 'pointer' }}>Dostawca{sortIndicator('supplier')}</th>
                    <th onClick={() => handleSort('business_days')} style={{ cursor: 'pointer' }}>Dni robocze{sortIndicator('business_days')}</th>
                    <th title="Ręczna wartość w dniach z okresem ważności">Korekta</th>
                  </tr>
                </thead>
                <tbody>
                  {pagination.pageItems.map(item => (
                    <tr key={item.item_number}>
                      <td><strong>{item.item_number}</strong></td>
                      <td>{item.item_name}</td>
                      <td>{item.supplier}</td>
                      <td>{item.business_days}</td>
                      <td><BlindsPredictionCorrection item={item} onChange={updateCorrection} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <TablePagination pagination={pagination} unfilteredTotal={data.length} />
          </>
        )}
      </div>
    </div>
  );
};

export default ProductionBlindsPredictions;
